import React from 'react';
import { Box, Button, Typography, Paper, Link } from '@mui/material';
import { useAuth } from '../context/AuthContext';
import LoadingOverlay from './LoadingOverlay';
import { authConfig } from '../config/auth';

interface AuthGuardProps {
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

export const AuthGuard = ({ children, fallback }: AuthGuardProps) => {
  const { isAuthenticated, loading, login } = useAuth();

  const handleClearSavedSettings = () => {
    authConfig.clearSavedAudience();
    // Force a page refresh to pick up the cleared settings
    window.location.reload();
  };

  if (loading) {
    return <LoadingOverlay message="Checking authentication..." />;
  }

  if (!isAuthenticated) {
    if (fallback) {
      return <>{fallback}</>;
    }
    
    const savedAudience = authConfig.getSavedAudience();
    
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        bgcolor="background.default"
      >
        <Paper elevation={2} sx={{ p: 4, textAlign: 'center', maxWidth: 400 }}>
          <Typography variant="h4" gutterBottom color="primary">
            Authentication Required
          </Typography>
          <Typography variant="body1" sx={{ mb: 3 }} color="text.secondary">
            Please sign in to access this application.
          </Typography>
          <Button
            variant="contained"
            color="primary"
            size="large"
            onClick={login}
            sx={{ minWidth: 200, mb: 2 }}
          >
            {savedAudience ? `Sign In with ${savedAudience}` : 'Sign In with BRI'}
          </Button>
          {savedAudience && (
            <Box sx={{ mt: 2 }}>
              <Link
                component="button"
                variant="body2"
                onClick={handleClearSavedSettings}
                sx={{ 
                  cursor: 'pointer',
                  textDecoration: 'underline',
                  color: 'text.secondary',
                  '&:hover': {
                    color: 'primary.main'
                  }
                }}
              >
                Clear saved settings
              </Link>
            </Box>
          )}
        </Paper>
      </Box>
    );
  }

  return <>{children}</>;
};

export default AuthGuard; 